"use client";

// GroundDial — the ground-colour picker seated in the shell's bottom-left, on
// the same line as share / options. Closed, it's a single orb carrying the
// current ground (a soft top-lit gradient of the colour itself, ringed so it
// reads as a chip on any ground). Tapped, the dock steps aside and the shared
// ArcPicker blooms out of the corner — hue · saturation · brightness ·
// gradient. Dragging a knob mints a new ground and paints it LIVE (page +
// bloom, at 60fps); the account only persists on release.
//
// The instrument itself lives in ArcPicker — the SAME control the folder and
// settings pickers use, just in its corner orientation. The only thing this
// file owns is what a GROUND means: the live paint, the ink regime flip, the
// one gradient (lib/ground.ts), and persistence through the theme.
//
// CONTROLLED mode (`value` + `onCommit`) points the same dial at a ground that
// isn't yours — a room's, which its owner mints for everyone standing in it.
// The paint is identical; only where the pick LANDS changes.

import * as React from "react";
import { useTheme } from "../ThemeProvider";
import { hsvToRgb, rgbToHsv, rgbToHex } from "../ColorInstrument";
import { inkRegime } from "../../lib/theme";
import type { RGB } from "../../lib/organism/color";
import ArcPicker, { ColorOrb, arcGeom, type Hsv } from "./ArcPicker";
import { applyGroundGradient, readGradLevel, saveGradLevel, setGroundLive } from "../../lib/ground";

/** other surfaces ask for the dial by name — the options menu's "ground" row,
 *  and netti's first word in settings to someone still on their minted ground. */
export const GROUND_DIAL_EVENT = "innernet:ground-dial";

export default function GroundDial({
  value,
  grad: gradIn,
  onCommit,
}: {
  /** CONTROLLED mode — the dial edits THIS ground (e.g. a room's) and reports
   *  picks via onCommit, leaving the user's own account theme alone. */
  value?: RGB;
  /** controlled mode's gradient level (0..1) — so the arcs open showing the
   *  bend the surface is already wearing, not a flat ground. */
  grad?: number;
  /** the pick on release: the ground, and the gradient level beside it so a
   *  surface that keeps its own ground can keep the bend with it. */
  onCommit?: (rgb: RGB, grad: number) => void;
} = {}): React.JSX.Element {
  const theme = useTheme();
  const controlled = !!onCommit;
  const [open, setOpen] = React.useState(false);
  const [narrow, setNarrow] = React.useState(false);
  const [hsv, setHsv] = React.useState<Hsv>(() => rgbToHsv(value ?? theme.bgRgb));
  const [grad, setGrad] = React.useState(gradIn ?? 0);
  React.useEffect(() => { if (gradIn !== undefined) setGrad(gradIn); }, [gradIn]);

  const scale = narrow ? 0.7 : 1;
  const g = React.useMemo(() => arcGeom(scale, "bl"), [scale]);

  // presence: mark the shell so the dock slides right to seat the orb, and hide
  // it entirely while the arcs are out.
  React.useEffect(() => {
    document.body.classList.add("gd-present");
    return () => { document.body.classList.remove("gd-present", "gd-open"); };
  }, []);
  React.useEffect(() => {
    document.body.classList.toggle("gd-open", open);
  }, [open]);

  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // escape settles the arcs back
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // any surface can ask the dial to bloom — the options menu's "ground colour"
  // row opens it right here rather than minting a second picker of its own.
  React.useEffect(() => {
    const onAsk = (e: Event) => {
      const want = (e as CustomEvent<{ open?: boolean }>).detail?.open;
      setOpen((o) => (want === undefined ? !o : want));
      if (want !== false) setHsv(rgbToHsv(value ?? theme.bgRgb));
    };
    window.addEventListener(GROUND_DIAL_EVENT, onAsk as EventListener);
    return () => window.removeEventListener(GROUND_DIAL_EVENT, onAsk as EventListener);
  }, [theme.bgRgb, value]);

  // paint the minted ground everywhere, live — no React/account write on the
  // hot path. --bg drives the page + dock veil; the four gradient stops drive
  // the ONE ground gradient (and with it every frosted mask that samples it);
  // the drive bus repaints the bloom at 60fps; the ink regime flips with
  // luminance so text stays legible mid-drag.
  const paintLive = React.useCallback((h: number, s: number, v: number, gr: number) => {
    const rgb = hsvToRgb(h, s, v) as RGB;
    const root = document.documentElement;
    root.style.setProperty("--bg", `rgb(${Math.round(rgb[0] * 255)} ${Math.round(rgb[1] * 255)} ${Math.round(rgb[2] * 255)})`);
    const { inkSat, inkLight } = inkRegime(rgb);
    root.style.setProperty("--ink-sat", inkSat);
    root.style.setProperty("--ink-light", inkLight);
    applyGroundGradient(h, s, v, gr);
    // the organism reads the BASE colour, not the gradient
    window.dispatchEvent(new CustomEvent("innernet:organism-drive", { detail: { bg: rgb } }));
  }, []);

  // the arcs open on the bend this device saved (it persists like the colour, so
  // a reload keeps your gradient). Personal mode only — a controlled dial (a
  // room's ground) must not inherit your personal gradient.
  //
  // Only the LEVEL is read here. The wash itself is painted by whatever paints
  // the ground (the boot script, applyGroundVars), each from the ground it is
  // actually painting. This used to paint it too, from the provider's ground at
  // mount, and on a hydrating dashboard that is still the cream placeholder: it
  // ran after GroundSeed had adopted the person's colour and left a cream wash
  // over it, with nothing after it to put the right one back.
  React.useEffect(() => {
    if (controlled) return;
    const saved = readGradLevel();
    if (saved > 0.006) setGrad(saved);
  }, [controlled]);

  // release → persist once. Personal mode goes through the theme (account +
  // local + cross-device, gradient level alongside in localStorage); controlled
  // mode hands the pick to the caller, which saves it for everyone in the room.
  const commit = React.useCallback((next: Hsv, gr: number) => {
    const rgb = hsvToRgb(next.h, next.s, next.v) as RGB;
    if (onCommit) {
      onCommit(rgb, gr);
      return;
    }
    // the bend is saved FIRST: setBackground repaints the gradient from the
    // saved level, so saving it after left the stops on the OLD bend on release.
    saveGradLevel(gr);
    theme.setBackground({ id: `dial-${Math.round(next.h)}-${Math.round(next.s * 100)}-${Math.round(next.v * 100)}`, label: "yours", rgb });
  }, [theme, onCommit]);

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next) setHsv(rgbToHsv(value ?? theme.bgRgb)); // re-sync to the live ground on open
      return next;
    });
  };

  const orbColor = open ? rgbToHex(hsvToRgb(hsv.h, hsv.s, hsv.v)) : rgbToHex(value ?? theme.bgRgb);

  return (
    <div className={`ground-dial${open ? " is-open" : ""}`} data-noswipe>
      {open && (
        <>
          {/* the corner goes progressively soft behind the arcs, so whatever is
              on the page can't clash with the colours you're reading. */}
          <div className="gd-blur" aria-hidden><i /><i /><i /></div>
          <button type="button" className="gd-scrim" aria-label="close colour picker" onClick={() => setOpen(false)} />
          <ArcPicker
            value={hsv}
            grad={grad}
            gradient
            layout="bl"
            scale={scale}
            onLive={setGroundLive}
            onChange={(next, gr) => { setHsv(next); setGrad(gr); paintLive(next.h, next.s, next.v, gr); }}
            onCommit={commit}
            style={{ position: "absolute", left: -g.CX, bottom: -(g.H - g.CY), transformOrigin: `${g.CX}px ${g.CY}px` }}
          />
        </>
      )}

      {/* the orb — the current ground closed, the minted preview open (tap closes) */}
      <ColorOrb
        color={orbColor}
        size={open ? g.ORB_OPEN : g.ORB}
        open={open}
        onClick={toggle}
        label={open ? "close colour picker" : "ground colour"}
      />
    </div>
  );
}
